import Vue from "vue";
import Component from "vue-class-component";
import axios from "axios";
// Services
import { getStocks } from "@/services";
// Components
import { StockFilterBar, Overlay, LimitDialog } from "@/components/index";
// Entities
import {
  Warehouse,
  Stock,
  StocksFields,
  Blocking,
  Profile,
  Company,
} from "@/entities";

@Component({
  components: {
    StockFilterBar,
    Overlay,
    LimitDialog,
  },
})
export default class Stockage extends Vue {
  warehouses: Warehouse[] = [];
  stocks: Stock[] = [];

  client = this.company.codeLogistic;
  idCompany = this.company.id;
  integrateStepZero = this.company.integrateStepZero;
  optionPalette = this.company.optionPalette;

  stocksFields: StocksFields = {
    reference: "",
    palletNumber: "",
    designation: "",
    family: "",
    lot: "",
    warehouse: "",
  };

  overlay = false;

  get headers() {
    const baseHeaders = [
      {
        text: this.$t("Stock.table.reference"),
        class: "indigo--text text--darken-4",
        align: "start",
        value: "reference",
      },
      {
        text: this.$t("Stock.table.designation"),
        class: "indigo--text text--darken-4",
        value: "designation",
      },
      {
        text: this.$t("Stock.table.family"),
        class: "indigo--text text--darken-4",
        value: "family",
      },
      {
        text: this.$t("Stock.table.warehouse"),
        class: "indigo--text text--darken-4",
        value: "warehouse",
      },
      {
        text: this.$t("Stock.table.weight"),
        class: "indigo--text text--darken-4",
        value: "weight",
      },
      {
        text: this.$t("Stock.table.lot"),
        class: "indigo--text text--darken-4",
        value: "lot",
      },
      {
        text: this.$t("Stock.table.total"),
        class: "indigo--text text--darken-4",
        value: "total",
      },
      {
        text: this.$t("Stock.table.available"),
        class: "indigo--text text--darken-4",
        value: "available",
      },
      {
        text: this.$t("Stock.table.reserved"),
        class: "indigo--text text--darken-4",
        value: "reserved",
      },
      {
        text: this.$t("Stock.table.blocking"),
        class: "indigo--text text--darken-4",
        value: "blocked",
      },
    ];

    if (this.optionPalette) {
      baseHeaders.splice(1, 0, {
        text: this.$t("Stock.table.palletNumber"),
        class: "indigo--text text--darken-4",
        value: "palletNumber",
      });
      baseHeaders.splice(8, 0, {
        text: this.$t("Stock.table.arrivalDate"),
        class: "indigo--text text--darken-4",
        value: "arivalDate",
      });
    }

    return baseHeaders;
  }

  headersCSV = [
    {
      text: "Reference",
      value: "reference",
    },
    { text: "Designation", value: "designation" },
    { text: "Famille", value: "family" },
    { text: "Entrepot", value: "warehouse" },
    { text: "Poids unitaire", value: "weight" },
    { text: "Lot", value: "lot" },
    { text: "Quantite totale", value: "total" },
    { text: "Quantite disponible", value: "available" },
    { text: "Quantite reservee", value: "reserved" },
    { text: "Quantite bloquee", value: "blocked" },
    { text: "Num contenant EGO", value: "palletNumber" },
    { text: "Date d'entree", value: "arivalDate" },
  ];

  async loadStock(): Promise<void> {
    const showOverlay = this.$refs.overlay as Overlay;
    showOverlay.show();
    this.stocks = await getStocks({
      client: this.client,
      integrateStepZero: this.integrateStepZero,
      optionPalette: this.optionPalette,
      userId: this.profile.id,
      stocksFields: {
        reference:
          this.stocksFields.reference === ""
            ? undefined
            : this.stocksFields.reference,
        palletNumber:
          this.stocksFields.palletNumber === ""
            ? undefined
            : this.stocksFields.palletNumber,
        designation:
          this.stocksFields.designation === ""
            ? undefined
            : this.stocksFields.designation,
        family:
          this.stocksFields.family === ""
            ? undefined
            : this.stocksFields.family,
        warehouse:
          this.stocksFields.warehouse === ""
            ? undefined
            : this.stocksFields.warehouse,
        lot: this.stocksFields.lot === "" ? undefined : this.stocksFields.lot,
      },
    });
    this.stocks.forEach((date) => {
      date.arivalDate = new Date(date.arivalDate).toLocaleDateString("en-GB");
    });
    showOverlay.hide();
  }

  async search(stocksFields: StocksFields): Promise<void> {
    this.stocksFields = stocksFields;
    this.checkFields();
    await this.loadStock();
  }

  goToBlocking(item: Stock): void {
    this.$router.push({
      name: "Consultation du blocage",
      params: {
        reference: item.reference,
        warehouse: item.warehouse,
        lot: item.lot,
      },
    });
  }

  async allWarehouses(): Promise<void> {
    this.warehouses = (
      await axios.get(`/api/warehouses/company/${this.idCompany}`)
    ).data as Warehouse[];
  }

  mounted(): void {
    this.allWarehouses();
  }

  // ========== GETTERS ========== //

  get company(): Company {
    return this.$store.getters.company;
  }

  get profile(): Profile {
    return this.$store.getters.profile;
  }

  // ========== OTHERS METHODS ========== //

  colorRow(item: Stock): any {
    const position = this.stocks.indexOf(item, 0);
    if (position % 2 == 0) {
      return "light-green lighten-5";
    }
  }

  goToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  checkFields() {
    if (
      this.stocksFields.reference === "" &&
      this.stocksFields.designation === "" &&
      this.stocksFields.family === "" &&
      this.stocksFields.warehouse === "" &&
      this.stocksFields.lot === ""
    ) {
      this.showLimitDialog();
    }
  }

  showLimitDialog(): void {
    const dialog = this.$refs.limitDialog as LimitDialog;
    dialog.show();
  }

  // === EXPORT TO CSV === //

  async getStockCSV(): Promise<Stock[]> {
    await this.loadStock();
    const stocksCSV = this.stocks;
    const userId = this.profile.id;
    await axios.post("/api/stats/action", {
      userId,
      action: "Extraction des stocks en CSV",
    });
    return stocksCSV;
  }

  convertToCSV(json: any): string {
    if (json.lot === null || json.lot === undefined) {
      json.lot = "";
    }
    if (json.reference === null || json.reference === undefined) {
      json.reference = "";
    }
    if (json.palletNumber === null || json.palletNumber === undefined) {
      json.palletNumber = "";
    }
    if (json.arivalDate === null || json.arivalDate === undefined) {
      json.arivalDate = "Pas de données";
    }
    if (json.designation === null || json.designation === undefined) {
      json.designation = "";
    }
    if (json.family === null || json.family === undefined) {
      json.family = "";
    }
    if (json.warehouse === null || json.warehouse === undefined) {
      json.warehouse = "";
    }
    if (json.weight === null || json.weight === undefined) {
      json.weight = "";
    }
    if (json.total === null || json.total === undefined) {
      json.total = "";
    }
    if (json.available === null || json.available === undefined) {
      json.available = "";
    }
    if (json.reserved === null || json.reserved === undefined) {
      json.reserved = "";
    }
    if (json.blocked === null || json.blocked === undefined) {
      json.blocked = "";
    }
    json.designation = json.designation.replace(/ /g, "");
    json.weight = json.weight.toString().replace(/\./g, ",");
    return `"${json.reference}";${json.designation}";"${json.family}";"${json.warehouse}";"${json.weight}";"${json.lot}";"${json.total}";"${json.available}";"${json.reserved}";"${json.blocked}";"${json.palletNumber}";"${json.arivalDate}"`;
  }

  downloadCSV(): void {
    const csvJSON = this.stocks.map((json: any) => this.convertToCSV(json));
    csvJSON.unshift(
      this.headersCSV.map((header: any) => header.text).join(";")
    );
    const csvData = csvJSON.join("\n");
    const csvBlob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
    const csvUrl = URL.createObjectURL(csvBlob);
    const link = document.createElement("a");
    link.href = csvUrl;
    link.download = "Stock.csv";
    link.click();
  }
}
